import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import { PiFadersHorizontalLight, PiTrashLight, PiLockKeyLight } from 'react-icons/pi';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import FileUploader from '../../shared/TailwindComponents/FileUploader';
import { 
  addMatterDraftAttachment, 
  getMatterDraftSettings, 
  removeMatterDraftAttachment, 
  setMatterCustomInstruction, 
  updateMatterDraftSettings,
  fetchMatterDraftTemplates
} from '../../../store/actions/Drafts/MatterDraftActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';

interface DraftSettingsSidePanelProps {
  matterId: string; 
  draftId: number;
  onClose: () => void;
}

const DraftSettingsSidePanel: React.FC<DraftSettingsSidePanelProps> = ({ matterId, draftId, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { hasSectionAccess, checkSectionGate } = useLicenseGate();
  // Structure / branding templates are the Document Templates (`templates`)
  // module. When the org hasn't licensed it, the selectors are replaced with an
  // upgrade prompt - the rest of the draft settings stay usable.
  const templatesLicensed = hasSectionAccess('templates');
  const [draftInstruction, setDraftInstruction] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [structureTemplates, setStructureTemplates] = useState<any[]>([]);
  const [brandingTemplates, setBrandingTemplates] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Only load templates for a licensed org - the picker is gated otherwise.
      const templates = templatesLicensed ? await dispatch(fetchMatterDraftTemplates()) : [];
      const settings = await dispatch(getMatterDraftSettings(matterId, draftId));

      formik.setFieldValue('DraftInstructions', settings.CustomSystemInstruction || '');
      formik.setFieldValue('StructureTemplateID', settings.StructureTemplateID || '');
      formik.setFieldValue('BrandingTemplateID', settings.BrandingTemplateID || '');
      setDraftInstruction(settings.CustomSystemInstruction || '');
      setAttachments(settings.Attachments || []);
      const structureTemps = templates.filter((t: any) => t.type === 'structure').map((t: any) => ({
        value: t.id,
        label: t.TemplateName,
      }));
      const brandingTemps = templates.filter((t: any) => t.type === 'branding').map((t: any) => ({
        value: t.id,
        label: t.TemplateName,
      }));

      setStructureTemplates(structureTemps);
      setBrandingTemplates(brandingTemps);
    } catch (error) {
      console.error('Failed to fetch matter draft settings:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to fetch matter draft settings'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [matterId, draftId, dispatch]);

  const formik = useFormik({
    initialValues: {
      DraftID: draftId,
      MatterID: matterId, 
      StructureTemplateID: '',
      BrandingTemplateID: '',
      DraftInstructions: '',
    },
    validationSchema: Yup.object({
      DraftID: Yup.number().required(),
      MatterID: Yup.string().required(),
      StructureTemplateID: Yup.string(),
      BrandingTemplateID: Yup.string(),
      DraftInstructions: Yup.string(),
    }),
    onSubmit: async (values) => {
      setIsSaving(true);
      try {
        if (values.DraftInstructions !== draftInstruction) {
          await dispatch(setMatterCustomInstruction(matterId, draftId, values.DraftInstructions));
          setDraftInstruction(values.DraftInstructions);
        }
        await dispatch(updateMatterDraftSettings({
          matter_id: matterId,
          draft_id: draftId,
          structure_template_id: values.StructureTemplateID,
          branding_template_id: values.BrandingTemplateID,
        }));
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Matter document settings updated'),
          modalType: 'success',
          duration: 3000,
        }));
        onClose(); 
      } catch (error) {
        console.error('Failed to save matter draft settings:', error);
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Failed to save matter draft settings'),
          modalType: 'error',
          duration: 3000,
        }));
      } finally {
        setIsSaving(false);
      }
    },
  });

  const handleEditInstruction = () => {
    dispatch(showModal({
      type: 'editDraftInstruction',
      message: t('Edit Custom Matter Document Instruction'),
      inputData: formik.values.DraftInstructions,
      onConfirm: async (newInstruction: string) => {
        formik.setFieldValue('DraftInstructions', newInstruction);
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      showModal: true,
    }));
  };

  const handleUpload = async (newFiles: File[]) => {
    try {
      for (const file of newFiles) {
        await dispatch(addMatterDraftAttachment(matterId, draftId, file));
      }
      const settings = await dispatch(getMatterDraftSettings(matterId, draftId));
      setAttachments(settings.Attachments || []);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Matter document attachment added successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (error) {
      console.error('Failed to upload matter document files:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to upload matter document files'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  const handleRemoveAttachment = async (index: number) => {
    try {
      await dispatch(removeMatterDraftAttachment(matterId, draftId, index));
      const settings = await dispatch(getMatterDraftSettings(matterId, draftId));
      setAttachments(settings.Attachments || []);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Matter document attachment removed successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (error) {
      console.error('Failed to remove matter document attachment:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to remove matter document attachment'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      <form id="draft-setting" onSubmit={formik.handleSubmit}>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 flex flex-col gap-4">
            {templatesLicensed ? (
              <>
                <Dropdown
                  label={t('Select Structure Template')}
                  options={structureTemplates}
                  value={formik.values.StructureTemplateID}
                  onChange={(val) => formik.setFieldValue('StructureTemplateID', val)}
                  placeholder={t('Select structure template')}
                  infoText={t('Select a customized template and get your matter document structured the way you want.')}
                />
                <Dropdown
                  label={t('Select Branding Template')}
                  options={brandingTemplates}
                  value={formik.values.BrandingTemplateID}
                  onChange={(val) => formik.setFieldValue('BrandingTemplateID', val)}
                  placeholder={t('Select branding template')}
                  infoText={t('Select a customized template and get your own branding and use custom font types.')}
                />
              </>
            ) : (
              <button
                type="button"
                data-testid="draft-templates-upgrade"
                onClick={() => checkSectionGate('templates')}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-start text-sm
                           border border-dashed border-gray-300 dark:border-gray-600
                           text-gray-500 dark:text-gray-400
                           hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <PiLockKeyLight className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1">
                  {t('Structure and branding templates are not included in your plan.')}
                </span>
                <span className="font-medium text-info-600 dark:text-info-400 flex-shrink-0">
                  {t('Upgrade')}
                </span>
              </button>
            )}
            <h3 className="text-lg font-medium mb-2">{t('Custom Matter Document Instruction')}</h3>
            <div className={`relative p-2 bg-gray-100 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 ${formik.values.DraftInstructions ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition duration-200' : ''}`}>
              {formik.values.DraftInstructions && <PiFadersHorizontalLight className="absolute top-2 start-2 w-4 h-4 text-gray-500" />}
              <p
                className={`text-sm text-gray-600 dark:text-gray-400 ${formik.values.DraftInstructions ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 ps-6 transition duration-200' : ''}`}
                onClick={formik.values.DraftInstructions ? handleEditInstruction : undefined}
                title={formik.values.DraftInstructions ? t('Click to edit instruction') : undefined}
              >
                {formik.values.DraftInstructions ? formik.values.DraftInstructions.substring(0, 100) + (formik.values.DraftInstructions.length > 100 ? '...' : '') : t('No custom instruction set')}
              </p>
            </div>
            {!formik.values.DraftInstructions && (
              <Button variant="outline" size="small" onClick={handleEditInstruction} className="mt-2">
                <PiFadersHorizontalLight className="w-4 h-4 me-1" />
                {t('Edit Instruction')}
              </Button>
            )}
          </div>
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">{t('Attachments')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {t('Upload files containing relevant information for this specific matter document.')}
            </p>
            {loading ? (
              <p>{t('Loading...')}</p>
            ) : attachments.length > 0 ? (
              <ul className="space-y-2 mb-4">
                {attachments.map((attachment, index) => (
                  <li key={index} className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded">
                    <span>{attachment.FileName}</span>
                    <Button variant="neutral" size="small" onClick={() => handleRemoveAttachment(index)}>
                      <PiTrashLight className='text-danger-400 w-5 h-5' />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-4">{t('No attachments uploaded yet.')}</p>
            )}
            <FileUploader files={[]} onFilesChange={handleUpload} />
          </div>
        </div>

        <div className="p-4 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700">
          <Button onClick={onClose} variant="outline" size="small">
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? t('Saving...') : t('Save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DraftSettingsSidePanel;